import { yupResolver } from '@hookform/resolvers/yup';
import type { RetailerRegionView } from '@vizit/api';
import { Button, ButtonLevel, SelectedFileStatus } from '@vizit/atoms';
import { FormDialog, FormDropZone, FormSelect, FormTextField } from '@vizit/forms';
import { PlusIcon } from '@vizit/icons';
import { SelectItem } from '@vizit/molecules';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import * as yup from 'yup';
import { FormProductCategoryComboBox, type FormProductCategoryComboBoxProps } from '../form-product-category-combo-box';

const formSchema = yup.object({
  file: yup.mixed<File>().required('A hero image is required'),
  retailerId: yup
    .string()
    .when('$showRetailers', ([showRetailers], s) => (showRetailers ? s.required('Retailer is required') : s)),
  category: yup.string().required('Category is required'),
  productTitle: yup.string().required('Product title is required'),
  brand: yup.string(),
});

type CreateANewProductFormSchema = {
  file: File;
  retailerId?: string;
  category: string;
  productTitle: string;
  brand?: string;
};

interface CreateANewProductDialogProps {
  /**
   * Has a submission error
   *
   * @optional
   * @type {boolean}
   */
  hasOnSubmissionError?: boolean;
  /**
   * Default values for the form
   */
  defaultValues?: CreateANewProductFormSchema;
  /**
   * Items for the retailer select dropdown
   */
  retailers?: RetailerRegionView[];
  /**
   * Query props for the product category combo box
   */
  productCategoryComboBoxProps: Omit<
    FormProductCategoryComboBoxProps<CreateANewProductFormSchema>,
    'control' | 'name' | 'label' | 'isRequired'
  >;
  /**
   * Upload status from the form to display in the dialog
   */
  uploadStatus?: SelectedFileStatus;
  /**
   * Whether the dialog is open
   */
  isOpen?: boolean;
  /**
   * Callback to notify when the dialog open state changes
   * @param open - Whether the dialog should be open
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally (e.g., by CallToActionCard)
   *
   * @optional
   * @type {boolean}
   */
  hideTrigger?: boolean;
  /**
   * The text for the trigger button
   *
   * @optional
   * @type {string}
   */
  triggerButtonText?: string;
  /**
   * Whether to use an icon for the trigger button
   *
   * @optional
   * @type {boolean}
   */
  useIcon?: boolean;
  /**
   * Callback function to handle the file status change
   * @param status - The status of the file
   */
  onFileStatusChange?: (status: SelectedFileStatus) => void;
  /**
   * Notifies the parent when the user picks a different retailer in the form,
   * so the parent can re-scope the category list.
   */
  onRetailerChange?: (retailerId: string) => void;
  /**
   * Callback function to handle the create new product api call
   */
  onSubmit: (data: CreateANewProductFormSchema) => Promise<void>;
}

function CreateANewProductDialog(props: CreateANewProductDialogProps) {
  const {
    defaultValues = {
      file: undefined,
      retailerId: '',
      productTitle: '',
      brand: '',
      category: '',
    },
    hasOnSubmissionError = false,
    uploadStatus,
    retailers,
    productCategoryComboBoxProps,
    isOpen,
    onOpenChange,
    hideTrigger = false,
    triggerButtonText = 'New product page',
    useIcon = false,
    onSubmit,
    onFileStatusChange,
    onRetailerChange,
  } = props;

  const showRetailerSelect = !!retailers?.length;

  const methods = useForm<CreateANewProductFormSchema>({
    resolver: yupResolver(formSchema, {
      context: { showRetailers: showRetailerSelect },
    }) as unknown as Resolver<CreateANewProductFormSchema>,
    defaultValues,
    mode: 'all',
    delayError: 650,
  });

  const { control, handleSubmit, setError, reset, clearErrors, watch } = methods;

  const watchedRetailerId = watch('retailerId');
  React.useEffect(() => {
    if (watchedRetailerId) {
      onRetailerChange?.(watchedRetailerId);
    }
  }, [watchedRetailerId, onRetailerChange]);

  const handleOnFileValidationError = (error?: string) => {
    if (error) {
      setError('file', { type: 'custom', message: error });
    }
  };

  const handleOnResetForm = () => {
    clearErrors();

    reset({
      file: undefined,
      retailerId: '',
      productTitle: '',
      brand: '',
      category: '',
    });

    // Reset the file status
    onFileStatusChange?.(SelectedFileStatus.Ready);
  };

  React.useEffect(() => {
    if (hasOnSubmissionError) {
      setError('productTitle', { message: 'Error creating new product. Please try again in a few minutes.' });
    }
  }, [hasOnSubmissionError, setError]);

  return (
    <FormDialog
      formName="create-new-product"
      formMethods={methods}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit(onSubmit)}
      onResetForm={handleOnResetForm}
      data-testid="create-product-dialog"
    >
      <FormDialog.Trigger>
        {hideTrigger ? (
          <Button className="sr-only" aria-hidden="true" isDisabled>
            Open dialog
          </Button>
        ) : (
          <Button level={ButtonLevel.Primary} aria-label={triggerButtonText}>
            {useIcon ? <PlusIcon size={20} /> : triggerButtonText}
          </Button>
        )}
      </FormDialog.Trigger>
      <FormDialog.Title>Create new product page</FormDialog.Title>
      <FormDialog.Subtitle>
        Enter the information for your new product page and upload images to get started.
      </FormDialog.Subtitle>
      <FormDialog.FormFields>
        {!!retailers?.length && (
          <FormSelect control={control} name="retailerId" label="Retailer" placeholder="Choose retailer" isRequired>
            {retailers.map((item) => (
              <SelectItem key={item.id} id={item.id} textValue={item.display_name}>
                {item.display_name}
              </SelectItem>
            ))}
          </FormSelect>
        )}
        <FormProductCategoryComboBox
          control={control}
          name="category"
          label="Category"
          isRequired
          menuTrigger="input"
          {...productCategoryComboBoxProps}
        />
        <FormTextField control={control} name="productTitle" label="Product title" isRequired />
        <FormTextField control={control} name="brand" label="Brand" />
        <FormDropZone
          label="Add a hero image"
          helperText="Carousel assets can be added once the product is created!"
          control={control}
          name="file"
          isRequired
          uploadStatus={uploadStatus}
          onFileValidationError={handleOnFileValidationError}
          onFileStatusChange={onFileStatusChange}
          dropZoneClassName="min-h-0"
          selectedFilesClassName="min-h-0"
        />
      </FormDialog.FormFields>
      <FormDialog.SubmitButton>Score my page!</FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { CreateANewProductDialog };
export type { CreateANewProductFormSchema, CreateANewProductDialogProps };
